/* eslint-disable max-len */
import { useEffect, useState } from "react";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import SchoolIcon from "@mui/icons-material/School";
import Check from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { ToastContainer, toast } from "react-toastify";
import styles from "./SchoolReward.module.css";
import starIcon from "../../assets/images/star.png";
import schoolRewardSimulatorHtml from "./schoolRewardSimulatorHtml";
import { openSignupWithMarketingParams } from "../../utils/signupMarketingUrl";

const FEATURE_CARDS = [
  {
    title: "No complicated setup",
    body: "Built directly into the core management module. Click to toggle on."
  },
  {
    title: "No extra workload for owners",
    body: "Tasks are validated automatically as teachers run lessons inside the app."
  },
  {
    title: "Smarter encouragement",
    body: "Builds high-integrity habits through instant feedback loops."
  }
];

const OUTCOMES = [
  "Better participation",
  "Better accountability",
  "Better consistency",
  "More engaged teachers"
];

const IMAGINE_PILLS = [
  "Uploaded lesson notes",
  "Attendance log is complete",
  "Assignments are set",
  "Parents are delighted",
  "Students experience continuous guidance"
];

const SCHOOL_PAIN_POINTS = [
  "Recruiting teachers every single academic session.",
  "Teachers resigning suddenly in the middle of a term.",
  "Parents questioning why another core teacher has left.",
  "Constantly begging teachers to upload lesson notes on time.",
  "Chasing daily attendance logs across multiple staff rooms.",
  "Wondering why some teachers only perform the bare minimum.",
  "Feeling like you're managing adults instead of leading professionals.",
  "Trying to improve WAEC/NECO results while staff keeps changing."
];

const TURNOVER_CARDS = [
  {
    title: "Recruiting Again",
    body:
      "You spend money on adverts, conduct multiple rounds of interviews, and strain your remaining staff — only to repeat the cycle next term.",
    footer: "Financial Leakage: Ad fees & lost time",
    footerEmoji: "⚠️",
    footerClass: "turnoverCardFooterRed",
    icon: ErrorOutlineIcon,
    iconClass: "turnoverCardIconRed"
  },
  {
    title: "Training Again",
    body:
      "You invest hours onboarding new teachers on your school's processes. Just when they're getting comfortable, they resign and you start over.",
    footer: "Time Loss: Repetitive onboarding",
    footerEmoji: "☑️",
    footerClass: "turnoverCardFooterOrange",
    icon: MenuBookOutlinedIcon,
    iconClass: "turnoverCardIconYellow"
  },
  {
    title: "Rebuilding Confidence",
    body:
      "Frustrated parents ask why results dropped. Constant teacher changes undermine trust — and can push families to withdraw their children.",
    footer: "Ultimate Danger: Student withdrawals",
    footerEmoji: "🔥",
    footerClass: "turnoverCardFooterDanger",
    icon: GroupsOutlinedIcon,
    iconClass: "turnoverCardIconPink"
  }
];

const TEACHER_FAQ = [
  {
    q: "Do I have to do extra work to earn rewards?",
    a: "No. Shelf21 adds points when you complete normal in-app tasks like attendance, lesson notes, and assignments — nothing beyond your usual workflow."
  },
  {
    q: "How do I start earning points?",
    a: "Use Shelf21 as you already do. Each eligible activity you complete inside the platform can add points to your account automatically."
  },
  {
    q: "Can these points really turn into money?",
    a: "Yes. Schools configure how points convert into monetary benefits and incentives. Your admin can share the active reward rules for your institution."
  },
  {
    q: "What makes this different from normal teacher incentives?",
    a: "Progress is tied to real activity in Shelf21, so recognition is transparent and consistent — not based on opinion or one-off contests."
  },
  {
    q: "What if I'm already doing these activities daily?",
    a: "That is the goal. The system rewards the work you are already doing, so consistency finally shows up in data and rewards."
  },
  {
    q: "Can my school join the Teacher Reward System?",
    a: "Schools using Shelf21 Pro can participate. School owners and admins can use Refer your School to get connected and configure the program."
  }
];

const INVITE_TASKS = [
  "Marking attendance",
  "Uploading lesson notes",
  "Creating assignments",
  "Adding exam questions",
  "Completing other eligible academic activities"
];

const AUTO_POINT_TAGS = ["No forms.", "No manual calculations.", "No extra work."];

const REDEEM_BENEFITS = [
  "Your teachers feel recognised for their consistency.",
  "Your school enjoys better participation, improved accountability, and a more engaged teaching team.",
  "Everyone wins."
];

const HOW_IT_WORKS_STEPS = [
  {
    title: "Book a Free Demo",
    body:
      "Start by booking a free demo with our team. We'll show you exactly how the Teacher Reward System works, answer your questions, and help you determine if it's the right fit for your school."
  },
  {
    title: "Activate Shelf21 Pro",
    body:
      "If you decide to move forward, simply activate the Shelf21 Pro Plan. The Teacher Reward System is included as part of the Pro Plan, so there's nothing extra to install or manage."
  },
  {
    title: "Invite Your Teachers",
    body:
      "Once your school is set up, invite your teachers to use Shelf21 for their everyday academic activities.",
    showTasks: true,
    footer:
      "These are tasks your teachers already do — they'll simply do them inside Shelf21."
  },
  {
    title: "Teachers Earn Points Automatically",
    body: "Every time a teacher completes an eligible activity, points are added automatically to their account.",
    showAutoTags: true,
    footer:
      "The more consistently they complete their daily responsibilities, the more points they earn."
  },
  {
    title: "Teachers Redeem Their Points",
    body:
      "At the end of each month, teachers can redeem their accumulated points for cash based on the current redemption rate available in the app.",
    showBenefits: true
  }
];

/** Looping confetti on the hero (right-hand side). Values are deterministic from index for stable hydration. */
const HERO_CONFETTI_COUNT = 34;

function heroConfettiStyle(i, compact) {
  const palettes = ["rgba(255,255,255,0.52)", "rgba(255,204,0,0.48)", "rgba(226,239,255,0.62)"];
  const leftPct = compact
    ? 40 + (((i * 47) >> 3) % 26)
    : 14 + (((i * 47) >> 3) % 72);
  const left = `${leftPct}%`;
  const w = `${3 + (i % 4)}px`;
  const h = `${5 + ((i >> 2) % 9)}px`;
  const driftMid = compact
    ? `${-2 + (((i * 19) >> 2) % 8)}px`
    : `${-8 + (((i * 19) >> 2) % 28)}px`;
  const driftEnd = compact
    ? `${5 + (((i * 31) >> 3) % 12)}px`
    : `${18 + (((i * 31) >> 3) % 36)}px`;
  const fade = `${0.34 + (((i * 13) >> 4) % 10) / 100}`;
  const durBase = compact ? 11 : 15;
  const durExtra = compact ? (((i * 37) >> 4) % 8) : (((i * 37) >> 4) % 12);

  return {
    left,
    width: w,
    height: h,
    backgroundColor: palettes[i % 3],
    borderRadius: i % 3 === 0 ? "50%" : `${1 + (i % 3)}px`,
    animationDuration: `${durBase + durExtra}s`,
    animationDelay: `${-(((i * 97) >> 5) % 160) / 10}s`,
    "--cfc-mid": driftMid,
    "--cfc-end": driftEnd,
    "--cfc-fade": fade
  };
}

const SchoolRewardPage = () => {
  const [heroConfettiCompact, setHeroConfettiCompact] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [simulatorIframeSrc, setSimulatorIframeSrc] = useState("");

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia("(max-width: 600px)");
    const onChange = () => setHeroConfettiCompact(mq.matches);

    onChange();
    mq.addEventListener("change", onChange);

    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (typeof schoolRewardSimulatorHtml !== "string" || !schoolRewardSimulatorHtml.length) {
      return;
    }
    let url = "";
    try {
      const blob = new Blob([schoolRewardSimulatorHtml], { type: "text/html;charset=utf-8" });
      url = URL.createObjectURL(blob);
      setSimulatorIframeSrc(url);
    } catch {
      setSimulatorIframeSrc("");
    }

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    document.title = "Teacher Reward System for School Owners | Shelf21";
  }, []);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const handleGetStarted = () => {
    openSignupWithMarketingParams({
      utm_source: "facebook",
      utm_campaign: "school_reward"
    });
  };

  const handleBookDemo = (e) => {
    e.preventDefault();
    if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
      window.Calendly.initPopupWidget({
        url: process.env.REACT_APP_CALENDLY_URL
      });
    } else {
      toast.info("Calendly widget is still loading. Please try again shortly.");
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className={styles.pageRoot}>
      <section className={styles.blueSection}>
        <div className={styles.heroConfettiBackdrop} aria-hidden>
          {Array.from({ length: HERO_CONFETTI_COUNT }).map((_, i) => (
            <span
              key={i}
              className={`${styles.heroConfettiPiece} ${heroConfettiCompact ? styles.heroConfettiPieceTight : ""}`}
              style={heroConfettiStyle(i, heroConfettiCompact)}
            />
          ))}
        </div>
        <div className={styles.container}>
          <div className={styles.heroBadgeOutline}>
            <SchoolIcon fontSize="small" aria-hidden />
            <span>Exclusive for Nigerian Private School Owners</span>
          </div>
          <h1 className={styles.heroTitle}>
            <span className={styles.highlightYellow}>School Owners:</span> Reward the Teachers who make Your School
            Exceptional
          </h1>
          <p className={styles.heroSubtitle}>
            Join hundreds of schools using Shelf21 to manage academics, operations, and communication—while giving
            teachers the opportunity to earn rewards for the work they do every day
          </p>
          <div className={styles.ctaRow}>
            <button type="button" className={styles.btnPrimary} onClick={handleGetStarted}>
              Sign Up
              <NorthEastIcon fontSize="small" aria-hidden />
            </button>
            <a href="#demo" className={styles.btnGhost} onClick={handleBookDemo}>
              Book a Demo
              <BookmarkBorderIcon fontSize="small" aria-hidden />
            </a>
          </div>
        </div>
      </section>

      <section className={styles.trsSection} aria-labelledby="school-reward-trs-heading">
        <div className={styles.trsShell}>
          <div className={`${styles.container} ${styles.centered}`}>
            <span className={styles.sectionBadge}>✨ What makes Shelf21 different</span>
            <h2 id="school-reward-trs-heading" className={styles.sectionTitle}>
              Introducing The <span className={styles.highlightBlue}>Shelf21 Teacher Reward System</span>
            </h2>
            <p className={styles.sectionLead}>
              A first-of-its-kind feature that transforms everyday teaching activities into opportunities for
              recognition. Teachers earn redeemable points for completing selected academic activities inside Shelf21.
            </p>

            <div className={styles.featureGrid}>
              {FEATURE_CARDS.map((card, i) => (
                <article key={card.title} className={styles.featureCard}>
                  <span className={styles.featureCardNum} aria-hidden>
                    {i + 1}
                  </span>
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>

            <div className={styles.outcomesBox}>
              <span className={styles.outcomesLabel}>Guaranteed school outcomes</span>
              <div className={styles.outcomePills}>
                {OUTCOMES.map((label) => (
                  <span key={label} className={styles.outcomePill}>
                    <span className={styles.outcomeCheck} aria-hidden>
                      <Check style={{ fontSize: 14 }} />
                    </span>
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.simulatorIntro}>
              <span className={styles.sectionBadge}>✨ Interactive experience playground</span>
              <h3 className={styles.sectionTitle}>See How It Works in Real-Time</h3>
              <p className={styles.sectionLead}>
                We&apos;ve built an interactive mockup of Mrs. Adebayo&apos;s daily checklist. Watch it run
                automatically to see how teachers earn points and redeem rewards inside Shelf21.
              </p>
            </div>

            <div className={styles.simulatorFrame}>
              {simulatorIframeSrc ? (
                <iframe
                  title="Shelf21 teacher reward system interactive simulator"
                  className={styles.simulatorIframe}
                  src={simulatorIframeSrc}
                  sandbox="allow-scripts"
                />
              ) : (
                <iframe
                  title="Shelf21 teacher reward system interactive simulator"
                  className={styles.simulatorIframe}
                  srcDoc={schoolRewardSimulatorHtml}
                  sandbox="allow-scripts"
                />
              )}
            </div>

            <div className={styles.imagineSection}>
              <h3 className={styles.imagineTitle}>
                Imagine walking into your school knowing Teachers have already ...
              </h3>
              <div className={styles.imaginePills}>
                {IMAGINE_PILLS.map((label) => (
                  <span key={label} className={styles.imaginePill}>
                    <span className={styles.imagineDot} aria-hidden />
                    {label}
                  </span>
                ))}
              </div>
              <p className={styles.imagineClosing}>
                All without a single headache, because teachers are actively seeking success inside{" "}
                <strong>Shelf21</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.painPointsSection} aria-labelledby="school-pain-points-heading">
        <div className={styles.container}>
          <h2 id="school-pain-points-heading" className={styles.painPointsTitle}>
            Is This Happening in Your School?
          </h2>
          <p className={styles.painPointsLead}>
            Managing a school is complex, but managing uncommitted or constantly changing staff is exhausting. If any
            of these sound familiar, you&apos;re exactly who we built this for:
          </p>
          <div className={styles.painPointsGridWrap}>
            <div className={styles.painPointsGrid}>
              {SCHOOL_PAIN_POINTS.map((point) => (
                <div key={point} className={styles.painPointCard}>
                  <span className={styles.painPointBullet} aria-hidden />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.turnoverSection} aria-labelledby="school-turnover-heading">
        <div className={styles.container}>
          <div className={styles.centered}>
            <span className={styles.turnoverBadge}>
              <AccessTimeIcon fontSize="inherit" aria-hidden />
              The hidden school headache
            </span>
            <h2 id="school-turnover-heading" className={styles.turnoverTitle}>
              Why School Owners Are Paying Attention
            </h2>
            <p className={styles.turnoverLead}>
              Teacher turnover is one of the biggest hidden costs in private education.
            </p>
          </div>

          <div className={styles.turnoverCardGrid}>
            {TURNOVER_CARDS.map((card) => {
              const CardIcon = card.icon;

              return (
                <article key={card.title} className={styles.turnoverCard}>
                  <span className={`${styles.turnoverCardIconWrap} ${styles[card.iconClass]}`} aria-hidden>
                    <CardIcon fontSize="small" />
                  </span>
                  <h3 className={styles.turnoverCardTitle}>{card.title}</h3>
                  <p className={styles.turnoverCardBody}>{card.body}</p>
                  <div className={`${styles.turnoverCardFooter} ${styles[card.footerClass]}`}>
                    <span aria-hidden>{card.footerEmoji}</span>
                    <span>{card.footer}</span>
                  </div>
                </article>
              );
            })}
          </div>

          <div className={styles.cultureCallout}>
            <img src={starIcon} alt="" className={styles.cultureCalloutBgStar} aria-hidden />
            <div className={styles.cultureCalloutIconWrap}>
              <img src={starIcon} alt="" className={styles.cultureCalloutIcon} />
            </div>
            <p className={styles.cultureCalloutText}>
              The schools that consistently keep great teachers aren&apos;t lucky. They intentionally build cultures
              where teachers feel recognised and valued. Shelf21 helps make that culture easier to build.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.howItWorksSection} aria-labelledby="school-how-it-works-heading">
        <div className={styles.container}>
          <div className={styles.centered}>
            <span className={styles.howItWorksBadge}>How it works</span>
            <h2 id="school-how-it-works-heading" className={styles.howItWorksTitle}>
              How Your School Can Start Rewarding Teachers in 5 Simple Steps
            </h2>
            <p className={styles.howItWorksLead}>
              It only takes five simple steps to activate the Shelf21 Teacher Reward System in your school.
            </p>
          </div>

          <div className={styles.howItWorksSteps}>
            {HOW_IT_WORKS_STEPS.map((step, i) => (
              <article key={step.title} className={styles.howItWorksStepCard}>
                <span className={styles.howItWorksStepNum} aria-hidden>
                  {i + 1}
                </span>
                <div className={styles.howItWorksStepContent}>
                  <h3 className={styles.howItWorksStepTitle}>{step.title}</h3>
                  <p className={styles.howItWorksStepBody}>{step.body}</p>

                  {step.showTasks ? (
                    <div className={styles.howItWorksTaskBox}>
                      <p className={styles.howItWorksTaskBoxLabel}>This includes tasks like:</p>
                      <ul className={styles.howItWorksTaskList}>
                        {INVITE_TASKS.map((task) => (
                          <li key={task}>{task}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {step.showAutoTags ? (
                    <div className={styles.howItWorksTagRow}>
                      {AUTO_POINT_TAGS.map((tag) => (
                        <span key={tag} className={styles.howItWorksTag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {step.showBenefits ? (
                    <ul className={styles.howItWorksBenefits}>
                      {REDEEM_BENEFITS.map((benefit, benefitIndex) => (
                        <li
                          key={benefit}
                          className={benefitIndex === REDEEM_BENEFITS.length - 1 ? styles.howItWorksBenefitHighlight : ""}
                        >
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {step.footer ? <p className={styles.howItWorksStepFooter}>{step.footer}</p> : null}
                </div>
              </article>
            ))}
            <article className={`${styles.howItWorksStepCard} ${styles.howItWorksClosingCard}`}>
              <span className={styles.howItWorksStepNumSpacer} aria-hidden />
              <div className={styles.howItWorksClosingContent}>
                <h3 className={styles.howItWorksClosingTitle}>That&apos;s It.</h3>
                <p className={styles.howItWorksClosingBody}>
                  No complicated setup. No extra workload for your teachers. Just a simple system that encourages the
                  behaviours every school wants to see more often.
                </p>
                <div className={styles.howItWorksCtaRow}>
                  <button type="button" className={styles.howItWorksBtnPrimary} onClick={handleGetStarted}>
                    Sign Up
                    <NorthEastIcon fontSize="small" aria-hidden />
                  </button>
                  <a href="#demo" className={styles.howItWorksBtnGhost} onClick={handleBookDemo}>
                    Book a Demo
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className={styles.faqSection}>
        <div className={`${styles.container} ${styles.centered}`}>
          <span className={styles.sectionLabel}>FAQ</span>
          <h2 className={styles.sectionTitle}>
            Questions? We&apos;ve Got <span className={styles.highlightBlue}>Answers.</span>
          </h2>
          <div className={styles.faqList}>
            {TEACHER_FAQ.map((item, i) => (
              <div key={item.q} className={styles.faqItem}>
                <button
                  type="button"
                  className={styles.faqQuestion}
                  onClick={() => toggleFaq(i)}
                  aria-expanded={openFaqIndex === i}
                >
                  <span>{item.q}</span>
                  <span className={styles.faqIcon} aria-hidden>
                    {openFaqIndex === i ? "−" : "+"}
                  </span>
                </button>
                {openFaqIndex === i ? (
                  <div className={styles.faqAnswer}>{item.a}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ToastContainer />
    </div>
  );
};

export default SchoolRewardPage;
